import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  Typography,
  Collapse,
  Box,
  CssBaseline,
  Container,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tabs,
  Tab,
  Modal,
  Alert,
  useMediaQuery,
  useTheme,
  Divider
} from "@mui/material";
import { Print, Delete,Info, Edit, ExpandMore, ExpandLess } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import Layout from "./Layout";
import { enGB } from "date-fns/locale";
import { startOfToday, startOfDay, endOfDay } from "date-fns";
import { collection, query, where, getDocs, updateDoc, deleteDoc, orderBy, doc, setDoc, writeBatch, getDoc } from "firebase/firestore";
import { db } from "./firebase"; // Update this path to your Firebase config
import { useAppContext } from "./AppContext";
import OrderSummaryPrint from "./components/OrderSummaryPrint";
import Snackbar from "@mui/material/Snackbar";
import { format,isValid} from "date-fns";
import EditOrderModal from './components/EditOrderModal';


const ViewOrders = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));
  const showTableView = !isMobile && !isTablet;
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(startOfToday());
  const [toDate, setToDate] = useState(startOfToday());
  const { shopId, userId, userName, t } = useAppContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [tabIndex, setTabIndex] = useState(0); 
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" or "error"
  const [isOffline, setIsOffline] = useState(false); // State to track offline status
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
 
  
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Add event listeners for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOffline(!navigator.onLine);

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    //Fetch from Firestore
    const fetchOrders = async () => {
      const ordersRef = collection(db, "orders");
  
      // Base query for date range and shopId
      let q = query(
        ordersRef,
        where("orderDate", ">=", startOfDay(fromDate)),
        where("orderDate", "<=", endOfDay(toDate)),
        where("shopId", "==", shopId),
        orderBy("orderDate", "desc")
      );
  
      // Apply additional filters based on search query
      if (searchQuery) {
        const isNumericQuery = !isNaN(searchQuery);
        if (isNumericQuery) {
          q = query(
            ordersRef,
            where("orderID", "==", searchQuery),
            where("shopId", "==", shopId)
          );
        } else {
          const trimmedQuery = searchQuery.trim();
          console.log("Trimmed Query",trimmedQuery);
          q = query(
            ordersRef,
            where("inscription", ">=", searchQuery),
            where("inscription", "<=", searchQuery + "\uf8ff"),
            where("shopId", "==", shopId)
          );
        }
      }
  
      try {
        const querySnapshot = await getDocs(q);
        const orders = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
  
        setFilteredData(orders);
  
        if (!searchQuery) {
          setOriginalData(orders);
        }
      } catch (error) {
        console.log(error);
      }
    };
  
    fetchOrders();
  }, [fromDate, toDate, shopId, searchQuery]);


  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChangeTab = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleDeleteConfirmed = async () => {
    if (orderToDelete) {
      try {
        console.log("To Delete",orderToDelete);
        // Step 1: Query to find the order document
        const orderQuerySnapshot = await getDocs(
          query(collection(db, "orders"), where("orderID", "==", orderToDelete))
        );
    
        // Since we expect only one order, get the first document
        const orderDoc = orderQuerySnapshot.docs[0];
        if (!orderDoc) {
          throw new Error("Order not found");
        }
    
        if (orderDoc.data().orderStatus === "Unfulfilled") {
          // Update the order status to "Canceled"
          await updateDoc(orderDoc.ref, { orderStatus: "Canceled" });
    
          // Step 2: Query and delete cashbook document. Make sure to use the correct field name and data type is number
          const cashbookQuerySnapshot = await getDocs(
            query(collection(db, "cashbook"), where("orderId", "==", Number(orderToDelete)))
          );
    
          // Get the first (and should be only) cashbook document
          const cashbookDoc = cashbookQuerySnapshot.docs[0];
          if (cashbookDoc) {
            await deleteDoc(cashbookDoc.ref);
          }
    
          // Step 3: Update the local state for UI
          const updatedData = originalData.map(order =>
            order.orderID === orderToDelete 
              ? { ...order, orderStatus: "Canceled" } 
              : order
          );
          setOriginalData(updatedData);
          setFilteredData(updatedData);
          setOpenDialog(false);
          setOrderToDelete(null);
          
          setSnackbarMessage(t('Order canceled successfully.'));
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage(t("Order status is not 'Unfulfilled', so it cannot be canceled."));
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error:", error);
        setSnackbarMessage(error.message === "Order not found" 
          ? t('Order not found.') 
          : t('Error updating order status or deleting cashbook entry.')
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };
  

  const handleOrderClick = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const filteredOrders = filteredData.filter(order =>
    tabIndex === 0 ? order.orderStatus !== "Canceled" : order.orderStatus === "Canceled"
  );

  const handleActionClick = async (action, orderId) => {
    const selectedOrder = filteredData.find(order => order.orderID === orderId);
    if (action === "print") {
      console.log("You clicked Print")
      setOrderDetails(selectedOrder);
      console.log(selectedOrder);
      setShowOrderSummary(true);
      
    } else if (action === "delete") {
      setOrderToDelete(orderId);
      setOpenDialog(true); // Open confirmation dialog
      //setOrderToDelete(order);
      //setOpenDialog(true);
    }
  };

  const formatToMyanmarCurrency = (number) => {
    const formattedNumber = new Intl.NumberFormat('my-MM').format(number);
    return `${formattedNumber}`;
  };

  const renderDeliveryPaymentType = ({ order }) => {
    if (order.deliveryFeeType === "Advance") {
      return (
        <>
          <Typography variant="subtitle1">
            <strong>{t("Delivery Charge Method")}</strong>: {t("Advance")}
          </Typography>
          <Typography variant="subtite1">
            <strong>{t("Delivery Charge")}</strong>: {order.deliveryFeeAmount} {t("MMK")}
          </Typography>
        </>
      );
    } else if (order.deliveryFeeType === "Collect") {
      return (
        <Typography variant="subtitle1">
          <strong>{t("Delivery Charge Method")}</strong>: {t("To Collect")}
        </Typography>
      );
    }
    return null;
  };

  const handleCloseOrderSummary = () => {
    // Cleanup state
   setShowOrderSummary(false);
   setOrderDetails(null);
   
 };

 const handleEditClick = (orderId) => {
  const order = filteredData.find(o => o.orderID === orderId);
  if (order.orderStatus === "Unfulfilled") {
    setSelectedOrder(order);
    setEditModalOpen(true);
    console.log("Selecte Order Data",order);
  } else {
    setSnackbarMessage(t("Only Unfulfilled orders can be edited"));
    setSnackbarSeverity("warning");
    setSnackbarOpen(true);
  }
};

const handleSaveEdit = async (updatedOrder, paymentInfo) => {
  try {
    // Calculate the remaining balance
    const totalPaid = updatedOrder.payments.reduce((sum, payment) => sum + payment.amount, 0);
    const remainingBalance = updatedOrder.total - totalPaid;

    // Create a batch using the imported writeBatch function
    const batch = writeBatch(db);

    // Reference to the order document
    const orderRef = doc(db, "orders", updatedOrder.id);

    // Prepare the data to save (ensure all fields are included)
    const orderToSave = {
      ...updatedOrder,
      cartItems: updatedOrder.cartItems || [],
      orderNotes: updatedOrder.orderNotes || '',
      inscription: updatedOrder.inscriptionText || updatedOrder.inscription || '',
      imageUrls: updatedOrder.imageUrls || [],
      payments: updatedOrder.payments || [{ method: 'Cash', amount: 0 }],
      paymentAmount: totalPaid, // Update paymentAmount
      remainingBalance: remainingBalance, // Update remainingBalance
      subtotal: updatedOrder.subtotal || 0,
      tax: updatedOrder.tax || 0,
      total: updatedOrder.total || 0,
      fulfillmentDate: updatedOrder.fulfillmentDate,
      fulfillmentTime: updatedOrder.fulfillmentTime,
    };

    // Add order update to batch, explicitly preserve Firestore Timestamp fields
    batch.set(orderRef, {
      ...orderToSave,
      fulfillmentDate: orderToSave.fulfillmentDate,
      fulfillmentTime: orderToSave.fulfillmentTime
    }, { merge: true });

    // Handle cashbook updates
    if (paymentInfo) {
      const { originalPayments, newPayments } = paymentInfo;

      // Track which payment methods we've processed
      const processedMethods = new Set();

      // Step 1: Query existing cashbook entries for this order
      const cashbookQuery = query(
        collection(db, "cashbook"),
        where("orderId", "==", Number(updatedOrder.orderID)),
        where("shopId", "==", shopId)
      );

      const cashbookDocs = await getDocs(cashbookQuery);
      const existingEntries = {};

      // Build a map of existing cashbook entries by payment method
      cashbookDocs.forEach(doc => {
        const data = doc.data();
        existingEntries[data.paymentMethod] = {
          ref: doc.ref,
          amount: data.amount
        };
      });

      // Step 2: Process each payment method in the updated order
      for (const payment of newPayments) {
        processedMethods.add(payment.method);

        if (existingEntries[payment.method]) {
          // Update existing cashbook entry
          batch.update(existingEntries[payment.method].ref, {
            amount: payment.amount,
            description: `Payment of Order #${updatedOrder.orderID} via ${payment.method}`,
            modifyDate: new Date(),
          });
        } else {
          // Create new cashbook entry for this payment method
          const newCashbookRef = doc(collection(db, "cashbook"));
          batch.set(newCashbookRef, {
            amount: payment.amount,
            description: `Payment of Order #${updatedOrder.orderID} via ${payment.method}`,
            orderId: Number(updatedOrder.orderID),
            paymentMethod: payment.method,
            shopId: shopId,
            timestamp: new Date(),
            transactionType: "income",
            userId: userId,
            userName: userName,
            modifyDate: new Date(),
          });
        }
      }

      // Step 3: Find payment methods that were removed
      for (const oldPayment of originalPayments) {
        if (!processedMethods.has(oldPayment.method) && existingEntries[oldPayment.method]) {
          // Delete cashbook entry for removed payment method
          batch.delete(existingEntries[oldPayment.method].ref);
        }
      }
    }

    // Commit all changes in a batch
    await batch.commit();

    // Re-fetch the updated order to ensure we have the correct Firestore Timestamps
    const updatedOrderRef = doc(db, "orders", updatedOrder.id);
    const updatedOrderSnapshot = await getDoc(updatedOrderRef);
    const freshOrderData = { ...updatedOrderSnapshot.data(), id: updatedOrderSnapshot.id };

    // Update local state with the freshly fetched data
    const updatedFilteredData = filteredData.map(order =>
      order.orderID === updatedOrder.orderID ? freshOrderData : order
    );
    const updatedOriginalData = originalData.map(order =>
      order.orderID === updatedOrder.orderID ? freshOrderData : order
    );

    setFilteredData(updatedFilteredData);
    setOriginalData(updatedOriginalData);

    setSnackbarMessage(t("Order updated successfully"));
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditModalOpen(false);
    setSelectedOrder(null);
  } catch (error) {
    console.error("Error updating order:", error);
    setSnackbarMessage(`${t("Error updating order")}: ${error.message}`);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  }
};

const formatDate = (timestamp) => {
    if (!timestamp) return t("No Date");
    // Handle Firestore Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isValid(date)) {
      return format(date, "dd-MM-yy");
    }
    return t("Invalid date");
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return t("No Time");
    // Handle Firestore Timestamp
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isValid(time)) {
      return format(time, "h:mm a");
    }
    return t("Invalid date");
  };

  const getStatusChipProps = (status) => {
    switch (status) {
      case "Fulfilled":
        return { color: "success", label: t("Fulfilled") };
      case "Unfulfilled":
        return { color: "warning", label: t("Unfulfilled") };
      case "Completed":
        return { color: "info", label: t("Completed") };
      case "Canceled":
        return { color: "error", label: t("Canceled") };
      default:
        return { color: "default", label: status ? t(status) : t("Unknown") };
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Layout>
      <Container sx={{m:0}}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Box mt={1}>
          {isOffline && (
            <Alert severity="warning" sx={{ marginTop: '10px' }}>
              {t("You are offline. Some features may not be available.")}
            </Alert>
          )}
          <Typography variant="h4" mt={3} mb={2}>
              {t("View Orders")}
            </Typography>
            <Tabs value={tabIndex} onChange={handleChangeTab} aria-label={t("Order Tabs")}>
              <Tab label={t('Active Orders')} />
              <Tab label={t('Canceled Orders')} />
            </Tabs>

            <Grid container spacing={2} alignItems="center" mt={1}>
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <DatePicker
                  label={t('From')}
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  inputFormat="dd/MM/yyyy"
                  slotProps={{ textField: { fullWidth: true, } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <DatePicker
                  label={t('To')}
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  inputFormat="dd/MM/yyyy"
                  slotProps={{ textField: { fullWidth: true, } }}
                />
              </Grid>
             
              <Grid item xs={12} sm={12} md={4}>
                <TextField
                  label={t('Search')}
                  variant="outlined"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("Order ID, Inscription etc.")}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* TABLE VIEW FOR DESKTOP */}
            {showTableView && (
            <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">{t("Order ID")}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">{t("Customer")}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">{t("Inscription")}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">{t("Status")}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">{t("Order Date")}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">{t("Fulfillment Date")}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight="bold">{t("Actions")}</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order) => {
                      return (
                      <React.Fragment key={order.orderID}>
                      <TableRow onClick={() => handleOrderClick(order.orderID)}
                      sx={{
                        cursor: "pointer",
                      }}>
                        <TableCell>{order.orderID}</TableCell>
                        <TableCell>{order.customerName} ({order.phoneNumber})</TableCell>
                        <TableCell>{order.inscription}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            {...getStatusChipProps(order.orderStatus)}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                       <TableCell>
                        {
                          // Check if orderDate is a Date object
                          (order.orderDate instanceof Date ? 
                            format(order.orderDate,"dd-MM-yyyy hh:mm a") : 
                            format(order.orderDate.toDate(),"dd-MM-yyyy hh:mm a")
                          )
                        } 
                       </TableCell>
                       <TableCell>
                        {formatDate(order.fulfillmentDate)} {formatTime(order.fulfillmentTime)}
                       </TableCell>
                        <TableCell>
                        {order.orderStatus === "Canceled" ? (
                          <IconButton color="info">
                            <Info />
                          </IconButton>
                        ) : (
                          <>
                            <IconButton onClick={() => handleActionClick("print", order.orderID)} color="primary">
                              <Print />
                            </IconButton>
                            <IconButton onClick={() => handleEditClick(order.orderID)} color="secondary">
                            <Edit />
                          </IconButton>
                            <IconButton onClick={() => handleActionClick("delete", order.orderID)} color="error">
                              <Delete />
                            </IconButton>
                          </>
                        )}
                        </TableCell>
                      </TableRow>

                        {/* Collapsed Detail Row */}
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0, border: "none" }}
                            colSpan={7}
                          >
                            <Collapse
                              in={expandedOrderId === order.orderID}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Card
                                variant="outlined"
                                sx={{ margin: 1, padding: 1 }} // Highlight overdue orders
                              >
                                
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>
                                        <Typography fontWeight="bold">
                                          {t("Item Name")}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography fontWeight="bold">
                                          {t("Quantity")}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography fontWeight="bold">
                                          {t("Price")}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography fontWeight="bold">
                                          {t("Amount")}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {order.cartItems.map((item, index) => (
                                      <TableRow key={index}>
                                        <TableCell>{item.name} {item.selectedVariant}</TableCell>
                                        <TableCell>{item.quantity}</TableCell>
                                        <TableCell>{formatToMyanmarCurrency(item.price+item.variantPrice)}</TableCell>
                                        <TableCell align="right">{formatToMyanmarCurrency(item.totalPrice)}</TableCell>
                                      </TableRow>
                                    ))}
                                    <TableRow>
                                    <TableCell colSpan={3} align="right" style={{padding:4,border:0}}><strong>{t("Subtotal")}</strong></TableCell>
                                    <TableCell align="right" style={{padding:4,border:0}}><strong>{formatToMyanmarCurrency(order.subtotal)}</strong></TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={3} align="right" style={{padding:4,border:0}}><strong>{t("Tax")}</strong></TableCell>
                                    <TableCell align="right" style={{padding:4,border:0}}><strong>{formatToMyanmarCurrency(order.tax)}</strong></TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={3} align="right" style={{padding:4,border:0}}><strong>{t("Delivery Charge")}</strong></TableCell>
                                    <TableCell align="right" style={{padding:4,border:0}}><strong>{formatToMyanmarCurrency(order.deliveryFeeAmount)}</strong></TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={3} align="right" style={{padding:4,border:0}}><strong>{t("Discount")}</strong></TableCell>
                                    <TableCell align="right" style={{padding:4,border:0}}><strong>{formatToMyanmarCurrency(order.discount)}</strong></TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={3} align="right" style={{padding:4,border:0}}><strong>{t("Total")}</strong></TableCell>
                                    <TableCell align="right" style={{padding:4,border:0}}><strong>{formatToMyanmarCurrency(order.total)}</strong></TableCell>
                                  </TableRow>
                                  
                                  {Array.isArray(order.payments) && order.payments.length > 0 && (
                                    order.payments.map((item, index) => (
                                      <TableRow key={index}>
                                        <TableCell colSpan={3} align="right" style={{ padding: 4, border: 0 }}>
                                          <strong>{t("Paid Amount")} ({item.method})</strong>
                                        </TableCell>
                                        <TableCell align="right" style={{ padding: 4, border: 0 }}>
                                          <strong>{formatToMyanmarCurrency(item.amount)}</strong>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                  
                                    <TableRow>
                                    <TableCell colSpan={3} align="right" style={{padding:4,border:0}}><strong>{t("Total Paid")}</strong></TableCell>
                                    <TableCell align="right" style={{padding:4,border:0}}><strong> {formatToMyanmarCurrency(order.payments?.reduce((sum, p) => sum + p.amount, 0) || 0)}</strong></TableCell>
                                  </TableRow>  
                                  <TableRow>
                                    <TableCell colSpan={3} align="right" style={{padding:4,border:0}}><strong>{t("Balance")}</strong></TableCell>
                                    <TableCell align="right" style={{padding:4,border:0}}><strong>{formatToMyanmarCurrency(Number(order.total)-Number(order.paymentAmount))}</strong></TableCell>
                                  </TableRow>
                                  </TableBody>
                                </Table>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ marginTop: 0 }}
                                >
                                  <strong>{t("Inscription Text")}</strong> {order.inscription}
                                </Typography>
                               
                                <Typography
                                  variant="subtitle1"
                                  sx={{ marginTop: 0 }}
                                >
                                  <strong>{t("Order Notes")}</strong> {order.orderNotes}
                                </Typography>

                                {order.deliveryAddress && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle1">
                                      <strong>{t("Delivery Address")}</strong> {order.deliveryAddress}
                                    </Typography>
                                   
                                    {renderDeliveryPaymentType({ order })}
                                   
                                  </Grid>
                                )}
                                 <Typography
                                  variant="subtitle1"
                                  sx={{ marginTop: 0 }}
                                >
                                  <strong>{t("Cashier")}</strong> {order.cashierName}
                                </Typography>
                                 <Box sx={{ marginTop: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {t("Images")}
                                </Typography>
                              {/*
                                {order.orderImages.map((image, index) => (
                                    <img
                                    src={image instanceof Blob || image instanceof File ? URL.createObjectURL(image) : image}
                                      alt={`Order attachment ${index + 1}`}
                                      style={{ borderRadius: 5}}
                                      loading="lazy"
                                      width="200"
                                      height="200"
                                    />
                                ))}
                             */}
                                 {(order.orderImages && order.orderImages.length > 0) ? (
                                  order.orderImages.map((image, index) => (
                                    <img
                                      src={image instanceof Blob || image instanceof File
                                        ? URL.createObjectURL(image)
                                        : image}
                                      alt={`Order attachment ${index + 1}`}
                                      style={{ borderRadius: 5 }}
                                      loading="lazy"
                                      width="200"
                                      height="200"
                                      key={index}
                                    />
                                  ))
                                ) : (
                                  (order.imageUrls && order.imageUrls.length > 0) ? (
                                    order.imageUrls.map((url, index) => (
                                      <img
                                        src={url}
                                        alt={`Order URL ${index + 1}`}
                                        style={{ borderRadius: 5 }}
                                        loading="lazy"
                                        width="200"
                                        height="200"
                                        key={index}
                                      />
                                    ))
                                  ) : (
                                    <p>{t("No images available")}</p>
                                  )
                                )}
                              </Box>
                              
                              </Card>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                    })}


                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25,50]}
              component="div"
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            />
            </>
            )}

            {/* CARD VIEW FOR MOBILE & TABLET */}
            {!showTableView && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {filteredOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((order) => (
                    <Grid item xs={12} key={order.orderID}>
                      <Card
                        sx={{
                          boxShadow: 2,
                          border: expandedOrderId === order.orderID ? `3px solid ${muiTheme.palette.primary.main}` : 'none',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: 4,
                          }
                        }}
                      >
                        <CardContent 
                          sx={{ pb: 1, cursor: 'pointer' }}
                          onClick={() => handleOrderClick(order.orderID)}
                        >
                          {/* Order Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: muiTheme.palette.primary.main }}>
                                {t("Order")} #{order.orderID}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                {order.customerName}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {order.phoneNumber}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Chip
                                {...getStatusChipProps(order.orderStatus)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          {/* Order Summary Info */}
                          <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {t("Order Date")}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {order.orderDate instanceof Date ? 
                                  format(order.orderDate,"dd-MM-yy") : 
                                  format(order.orderDate.toDate(),"dd-MM-yy")
                                }
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {t("Fulfillment")}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(order.fulfillmentDate)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {t("Total Amount")}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: muiTheme.palette.success.main }}>
                                {formatToMyanmarCurrency(order.total)}
                              </Typography>
                            </Grid>
                          </Grid>

                          {/* Inscription */}
                          {order.inscription && (
                            <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="caption" color="textSecondary" display="block">
                                {t("Inscription")}
                              </Typography>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {order.inscription}
                              </Typography>
                            </Box>
                          )}

                          {/* Expand/Collapse Button */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <IconButton
                              onClick={() => handleOrderClick(order.orderID)}
                              size="small"
                              sx={{ color: muiTheme.palette.primary.main }}
                            >
                              {expandedOrderId === order.orderID ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>
                        </CardContent>

                        {/* Expanded Content */}
                        <Collapse in={expandedOrderId === order.orderID} timeout="auto" unmountOnExit>
                          <Divider />
                          <CardContent sx={{ pt: 2 }}>
                            {/* Items Table */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                              {t("Items")}
                            </Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontSize: '0.875rem' }}>
                                      <Typography variant="caption" fontWeight="bold">{t("Item")}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '0.875rem' }}>
                                      <Typography variant="caption" fontWeight="bold">{t("Qty")}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.875rem' }}>
                                      <Typography variant="caption" fontWeight="bold">{t("Amount")}</Typography>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {order.cartItems.map((item, index) => (
                                    <TableRow key={index}>
                                      <TableCell sx={{ fontSize: '0.875rem', py: 0.75 }}>
                                        <Typography variant="caption">
                                          {item.name} {item.selectedVariant}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontSize: '0.875rem', py: 0.75 }}>
                                        <Typography variant="caption">{item.quantity}</Typography>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontSize: '0.875rem', py: 0.75 }}>
                                        <Typography variant="caption">
                                          {formatToMyanmarCurrency(item.totalPrice)}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>

                            {/* Order Totals */}
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Typography variant="caption" color="textSecondary">{t("Subtotal")}:</Typography>
                                <Typography variant="caption" fontWeight="bold">
                                  {formatToMyanmarCurrency(order.subtotal)}
                                </Typography>
                              </Box>
                              {order.tax > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                  <Typography variant="caption" color="textSecondary">{t("Tax")}:</Typography>
                                  <Typography variant="caption" fontWeight="bold">
                                    {formatToMyanmarCurrency(order.tax)}
                                  </Typography>
                                </Box>
                              )}
                              {order.deliveryFeeAmount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                  <Typography variant="caption" color="textSecondary">{t("Delivery")}:</Typography>
                                  <Typography variant="caption" fontWeight="bold">
                                    {formatToMyanmarCurrency(order.deliveryFeeAmount)}
                                  </Typography>
                                </Box>
                              )}
                              {order.discount > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                  <Typography variant="caption" color="textSecondary">{t("Discount")}:</Typography>
                                  <Typography variant="caption" fontWeight="bold">
                                    -{formatToMyanmarCurrency(order.discount)}
                                  </Typography>
                                </Box>
                              )}
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" fontWeight="bold">{t("Total")}:</Typography>
                                <Typography variant="body2" fontWeight="bold" color={muiTheme.palette.success.main}>
                                  {formatToMyanmarCurrency(order.total)}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Payment Info */}
                            {Array.isArray(order.payments) && order.payments.length > 0 && (
                              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0f8f5', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.75 }}>
                                  {t("Payment Details")}
                                </Typography>
                                {order.payments.map((payment, index) => (
                                  <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="caption" color="textSecondary">
                                      {payment.method}:
                                    </Typography>
                                    <Typography variant="caption" fontWeight="bold">
                                      {formatToMyanmarCurrency(payment.amount)}
                                    </Typography>
                                  </Box>
                                ))}
                                <Divider sx={{ my: 0.75 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" fontWeight="bold">{t("Total Paid")}:</Typography>
                                  <Typography variant="caption" fontWeight="bold">
                                    {formatToMyanmarCurrency(order.payments.reduce((sum, p) => sum + p.amount, 0))}
                                  </Typography>
                                </Box>
                                {order.remainingBalance > 0 && (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, color: '#d32f2f' }}>
                                    <Typography variant="caption" fontWeight="bold">{t("Balance Due")}:</Typography>
                                    <Typography variant="caption" fontWeight="bold">
                                      {formatToMyanmarCurrency(order.remainingBalance)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Additional Info */}
                            {order.orderNotes && (
                              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="bold" display="block">
                                  {t("Notes")}
                                </Typography>
                                <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                                  {order.orderNotes}
                                </Typography>
                              </Box>
                            )}

                            {order.deliveryAddress && (
                              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="bold" display="block">
                                  {t("Delivery Address")}
                                </Typography>
                                <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                                  {order.deliveryAddress}
                                </Typography>
                                {renderDeliveryPaymentType({ order })}
                              </Box>
                            )}

                            {/* Images */}
                            {((order.orderImages && order.orderImages.length > 0) || (order.imageUrls && order.imageUrls.length > 0)) && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                                  {t("Images")}
                                </Typography>
                                <Grid container spacing={1}>
                                  {(order.orderImages && order.orderImages.length > 0) ? (
                                    order.orderImages.map((image, index) => (
                                      <Grid item xs={6} sm={4} key={index}>
                                        <img
                                          src={image instanceof Blob || image instanceof File
                                            ? URL.createObjectURL(image)
                                            : image}
                                          alt={`Order attachment ${index + 1}`}
                                          style={{ borderRadius: 4, width: '100%', height: 'auto', maxHeight: 150 }}
                                          loading="lazy"
                                        />
                                      </Grid>
                                    ))
                                  ) : (
                                    order.imageUrls && order.imageUrls.length > 0 && order.imageUrls.map((url, index) => (
                                      <Grid item xs={6} sm={4} key={index}>
                                        <img
                                          src={url}
                                          alt={`Order URL ${index + 1}`}
                                          style={{ borderRadius: 4, width: '100%', height: 'auto', maxHeight: 150 }}
                                          loading="lazy"
                                        />
                                      </Grid>
                                    ))
                                  )}
                                </Grid>
                              </Box>
                            )}
                          </CardContent>

                          {/* Card Actions - Bottom Buttons */}
                          <Divider />
                          <CardActions sx={{ justifyContent: 'space-around', py: 1.5 }}>
                            {order.orderStatus === "Canceled" ? (
                              <IconButton color="info" fullWidth>
                                <Info />
                              </IconButton>
                            ) : (
                              <>
                                <Button
                                  size="small"
                                  startIcon={<Print />}
                                  onClick={() => handleActionClick("print", order.orderID)}
                                  color="primary"
                                  fullWidth
                                >
                                  {t("Print")}
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<Edit />}
                                  onClick={() => handleEditClick(order.orderID)}
                                  color="secondary"
                                  fullWidth
                                >
                                  {t("Edit")}
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<Delete />}
                                  onClick={() => handleActionClick("delete", order.orderID)}
                                  color="error"
                                  fullWidth
                                >
                                  {t("Cancel")}
                                </Button>
                              </>
                            )}
                          </CardActions>
                        </Collapse>
                      </Card>
                    </Grid>
                  ))}
              </Grid>

              {/* Pagination for Card View */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredOrders.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
              </Box>
            </Box>
            )}
          </Box>
        </LocalizationProvider>

        <Modal
        open={showOrderSummary}
        onClose={handleCloseOrderSummary}
        aria-labelledby="order-summary-modal"
        aria-describedby="order-summary-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 800,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <OrderSummaryPrint
            orderDetails={orderDetails}
            onClose={handleCloseOrderSummary}
          />
        </Box>
      </Modal>

      {/* Add EditOrderModal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        aria-labelledby="edit-order-modal"
        aria-describedby="edit-order-description"
        disableEnforceFocus // Add this to prevent focus trap issues
      >
        <EditOrderModal
          selectedOrder={selectedOrder}
          setEditModalOpen={setEditModalOpen}
          handleSaveEdit={handleSaveEdit}
        />
      </Modal>
        
        {/* Delete confirmation dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>{t('Confirm Deletion')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('Are you sure you want to cancel order {orderId}?', { orderId: orderToDelete?.orderID })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              {t('Cancel')}
            </Button>
            <Button onClick={handleDeleteConfirmed} color="secondary">
              {t('Confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      </Container>
      </Layout>
    </ThemeProvider>
   
  );
};

export default ViewOrders;
